import React, { useState } from "react";
import axios from "axios";
import useIsOpen from "../utils/useIsOpen";
import { useUserStore } from "../../stores/useUserStore";
import useErrorState from "../utils/useErrorState";
import useJWTCookie from "./useJWTCookie";

export type UseAxiosProps = {
    error: string;
    setError: (v: string) => boolean;
};

const useAxios = (params?: UseAxiosProps) => {
    const user = useUserStore((state) => state.user);
    const [loading, startLoad, endLoad] = useIsOpen();
    const { error, setError, clearError } = useErrorState();
    const { getJwt } = useJWTCookie();

    /**
     * 获取有效的认证令牌
     * 优先级：bearerToken > localforage中的JWT > user.refreshToken
     */
    const getAuthToken = async (bearerToken?: string): Promise<string | undefined> => {
        // 1. 如果直接传了令牌，使用它
        if (bearerToken && bearerToken.length > 0) {
            return `Bearer ${bearerToken}`;
        }

        // 2. 尝试从localforage获取JWT（页面刷新时使用）
        try {
            const [jwtToken] = await getJwt();
            if (jwtToken && jwtToken.length > 0) {
                console.log("[useAxios] Using JWT token from localforage");
                return `Bearer ${jwtToken}`;
            }
        } catch (e) {
            console.warn("[useAxios] Failed to get JWT from storage:", e);
        }

        // 3. 最后才使用user store中的令牌
        if (user.refreshToken && user.refreshToken.length > 0) {
            console.log("[useAxios] Using JWT token from user store");
            return `Bearer ${user.refreshToken}`;
        }

        console.warn("[useAxios] No authentication token available");
        return undefined;
    };

    const get = async <T,>(
        url: string,
        bearerToken?: string
    ): Promise<T | undefined> => {
        const token = await getAuthToken(bearerToken);
        
        try {
            startLoad();
            const headers: any = {
                Accept: '*/*'
            };
            // ✅ 仅在有令牌时才设置 Authorization header
            if (token) {
                headers.Authorization = token;
            }
            
            let response = await axios.get<T>(url, { headers });
            endLoad();
            return response.data;
        } catch (err: any) {
            params ? params.setError(err.message) : setError(err.message);
            endLoad();
            return undefined;
        }
    };

    const post = async <T,>(
        url: string,
        data?: any
    ): Promise<T | undefined> => {
        const token = await getAuthToken();
        
        try {
            startLoad();
            const headers: any = {
                Accept: '*/*'
            };
            // ✅ 仅在有令牌时才设置 Authorization header
            if (token) {
                headers.Authorization = token;
            }
            
            let response = await axios.post(url, data, { headers });
            endLoad();
            return response.data;
        } catch (err: any) {
            console.log(err)
            endLoad();
            params ? params.setError(err.message) : setError(err.message);
            return undefined;
        }
    };

    const put = async <T,>(url: string, data?: any): Promise<T | undefined> => {
        const token = await getAuthToken();
        
        try {
            startLoad();
            const headers: any = {};
            // ✅ 仅在有令牌时才设置 Authorization header
            if (token) {
                headers.Authorization = token;
            }
            
            let response = await axios.put(url, data, { headers });
            endLoad();
            return response.data;
        } catch (err: any) {
            endLoad();
            params ? params.setError(err.message) : setError(err.message);
            return undefined;
        }
    };

    const del = async <T,>(url: string) => {
        const token = await getAuthToken();
        
        try {
            startLoad();
            const headers: any = {};
            // ✅ 仅在有令牌时才设置 Authorization header
            if (token) {
                headers.Authorization = token;
            }
            
            let response = await axios.delete(url, { headers });
            endLoad();
            return response.data;
        } catch (err: any) {
            endLoad();
            params ? params.setError(err.message) : setError(err.message);
            return undefined;
        }
    };

    return {
        error,
        loading,

        get,
        post,
        put,
        del,
        clearError,
        setError,
    };
};

export default useAxios;
